import { z } from 'zod';

/**
 * Single typed config for the whole system. The agent loads
 * digger.config.json + env overrides; the console fetches the public subset
 * from GET /api/config. All tunables live here — no magic numbers in logic.
 */

const pin = z.number().int().min(0).max(27).nullable();

/**
 * One ICE server entry, config- and wire-visible (the relay mints and ships
 * these in signaling). Browser RTCPeerConnection accepts the array form of
 * `urls` directly; the agent flatMaps to werift's one-url-per-entry form.
 *
 * ⚠️ werift (0.22.x) only honours the FIRST `stun:` and FIRST `turn:` entry
 * and never matches `turns:` — lists destined for the agent must be exactly
 * [stun, turn-udp] with explicit ports. Browsers take the full list.
 */
export const IceServerSchema = z.object({
  urls: z.array(z.string()),
  username: z.string().optional(),
  credential: z.string().optional(),
});
export type IceServer = z.infer<typeof IceServerSchema>;

const jointPoseSchema = (rate: number, min: number, max: number, init: number) =>
  z
    .object({
      rateDegPerSec: z.number().default(rate),
      minDeg: z.number().default(min),
      maxDeg: z.number().default(max),
      initDeg: z.number().default(init),
    })
    .default({});
export type JointPoseConfig = {
  rateDegPerSec: number;
  minDeg: number;
  maxDeg: number;
  initDeg: number;
};

export const MotorPinsSchema = z.object({
  /** BCM pin numbers. null = TODO(hardware): not yet assigned. */
  pwm: pin,
  forward: pin,
  reverse: pin,
});
export type MotorPins = z.infer<typeof MotorPinsSchema>;

const unassignedMotor: MotorPins = { pwm: null, forward: null, reverse: null };

export const HatMotorSchema = z.object({
  /** Motor HAT terminal (M1–M4). null = TODO(hardware): not yet assigned. */
  motor: z.number().int().min(1).max(4).nullable(),
  /** Flip direction in software instead of swapping the terminal wires. */
  invert: z.boolean().default(false),
});
export type HatMotor = z.infer<typeof HatMotorSchema>;

const unassignedHatMotor: HatMotor = { motor: null, invert: false };

export const CameraConfigSchema = z.object({
  /**
   * none: no video (pure control dev).
   * rpicam: rpicam-vid, hardware H.264 on Pi 4 (assumed deployment).
   * ffmpeg: dev machines — webcam via avfoundation (macOS) / v4l2 (Linux).
   */
  source: z.enum(['none', 'rpicam', 'ffmpeg']).default('none'),
  /** rpicam: camera index ("0"/"1"). ffmpeg: device ("0" avfoundation, "/dev/video0" v4l2). */
  device: z.string().default('0'),
  width: z.number().int().default(1280),
  height: z.number().int().default(720),
  fps: z.number().int().default(30),
  bitrateKbps: z.number().int().default(1800),
  /** Keyframe interval in frames (with inline SPS/PPS for mid-stream join). */
  gop: z.number().int().default(60),
  /** Escape hatch: extra CLI args appended to the capture command. */
  extraArgs: z.array(z.string()).default([]),
});
export type CameraConfig = z.infer<typeof CameraConfigSchema>;

export const AgentConfigSchema = z.object({
  agent: z
    .object({
      host: z.string().default('0.0.0.0'),
      port: z.number().int().default(8080),
      logLevel: z.enum(['trace', 'debug', 'info', 'warn', 'error']).default('info'),
      /** Directory of the built operator console to serve statically, if present. */
      consoleDist: z.string().default('../operator-console/dist'),
    })
    .default({}),

  /** webrtc: media + control on one PeerConnection (the real path).
   *  mjpeg-ws: MJPEG over HTTP + control over WebSocket (bring-up/debug). */
  transport: z.enum(['webrtc', 'mjpeg-ws']).default('webrtc'),

  control: z
    .object({
      /** Console send rate. Snapshots of full input state, never queued events. */
      tickHz: z.number().int().min(5).max(100).default(30),
    })
    .default({}),

  safety: z
    .object({
      /** Agent stops the digger after this much control silence. */
      deadmanMs: z.number().int().min(50).default(250),
      /** How often the agent evaluates the deadman timer. */
      deadmanCheckMs: z.number().int().min(10).default(50),
    })
    .default({}),

  cameras: z
    .object({
      front: CameraConfigSchema.default({}),
      rear: CameraConfigSchema.default({}),
    })
    .default({}),

  webrtc: z
    .object({
      /** Empty on LAN (host candidates suffice). The relay supplies minted
       *  STUN/TURN per session on the internet path; anything set here is
       *  the fallback when the relay ships none. */
      iceServers: z.array(IceServerSchema).default([]),
      /** RTP payload size budget (fits well under a 1500 MTU with overhead). */
      mtu: z.number().int().default(1200),
    })
    .default({}),

  /**
   * Cloud signaling (internet release). When `url` is set the agent dials
   * OUT to the relay and keeps that link up forever — additive to the LAN
   * listener, no inbound ports on the digger's network. Requires
   * `agentKey` (authenticates the agent to the relay) and `auth.jwtSecret`
   * (the agent's own end-to-end check of operator tokens) — enforced below.
   */
  signaling: z
    .object({
      /** e.g. wss://relay.example.com — unset = LAN-only. */
      url: z.string().optional(),
      diggerId: z.string().default('digger-1'),
      /** Pre-shared secret identifying this digger to the relay. */
      agentKey: z.string().optional(),
      reconnectBaseMs: z.number().int().default(1000),
      reconnectMaxMs: z.number().int().default(30000),
    })
    .default({}),

  auth: z
    .object({
      /**
       * LAN-listener auth. Optional on a trusted LAN; the cloud signaling
       * path is independently ALWAYS token-checked (jwtSecret) — an
       * unauthenticated internet-exposed digger is unacceptable.
       */
      required: z.boolean().default(false),
      /** Static shared token accepted on the LAN listener. */
      token: z.string().optional(),
      /**
       * HS256 secret shared with the relay. The agent verifies operator
       * JWTs itself (signature + expiry) so a relay auth bug can't hand
       * out control.
       */
      jwtSecret: z.string().optional(),
    })
    .default({}),

  actuator: z
    .object({
      type: z.enum(['mock', 'gpio-pwm', 'pca9685']).default('mock'),
      /**
       * Adafruit DC & Stepper Motor HAT: PCA9685 PWM chip on I2C driving
       * TB6612 H-bridges. Four motor terminals (M1–M4), so at most four of
       * the six digger functions can be assigned; unassigned functions are
       * absent from the actuator's capabilities and ignored.
       */
      pca9685: z
        .object({
          /** I2C bus number (/dev/i2c-1 on every modern Pi). */
          busNumber: z.number().int().default(1),
          /** HAT address; 0x60 unless address jumpers are soldered. */
          address: z.number().int().default(0x60),
          /** PCA9685 PWM frequency. 1600 matches Adafruit MotorKit (the
           *  chip tops out at 1526 Hz; the prescaler clamps). */
          pwmFrequencyHz: z.number().int().default(1600),
          motors: z
            .object({
              leftTrack: HatMotorSchema.default(unassignedHatMotor),
              rightTrack: HatMotorSchema.default(unassignedHatMotor),
              slew: HatMotorSchema.default(unassignedHatMotor),
              boom: HatMotorSchema.default(unassignedHatMotor),
              dipper: HatMotorSchema.default(unassignedHatMotor),
              bucket: HatMotorSchema.default(unassignedHatMotor),
            })
            .default({}),
        })
        .default({}),
      gpioPwm: z
        .object({
          pwmFrequencyHz: z.number().int().default(8000),
          motors: z
            .object({
              leftTrack: MotorPinsSchema.default(unassignedMotor),
              rightTrack: MotorPinsSchema.default(unassignedMotor),
              slew: MotorPinsSchema.default(unassignedMotor),
              boom: MotorPinsSchema.default(unassignedMotor),
              dipper: MotorPinsSchema.default(unassignedMotor),
              bucket: MotorPinsSchema.default(unassignedMotor),
            })
            .default({}),
        })
        .default({}),
    })
    .default({}),

  /**
   * Dead-reckoning rates for the pose inset: how fast each axis moves at
   * full command. TODO(hardware): calibrate against the real machine (time
   * a full slew / boom sweep with a stopwatch); these are placeholders.
   * Joint limits are the mechanical range used to clamp the estimate.
   */
  pose: z
    .object({
      /** Heading change at full spin-in-place (left=+1, right=-1). */
      yawRateDegPerSec: z.number().default(30),
      slewRateDegPerSec: z.number().default(40),
      boom: jointPoseSchema(25, -30, 65, 35),
      dipper: jointPoseSchema(35, -150, -40, -95),
      bucket: jointPoseSchema(60, -120, 20, -45),
    })
    .default({}),

  hud: z
    .object({
      /**
       * Capture+encode latency added to the glass-to-glass estimate; not
       * remotely measurable, so calibrate once with the millisecond-clock
       * method (see README) and set it here.
       */
      captureEncodeMs: z.number().default(45),
    })
    .default({}),
}).superRefine((cfg, ctx) => {
  // Refuse to start rather than expose an unauthenticated digger: the cloud
  // dial-out is only allowed with both credentials in place.
  if (cfg.signaling.url && !cfg.signaling.agentKey) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['signaling', 'agentKey'],
      message: 'signaling.url is set but signaling.agentKey is missing',
    });
  }
  if (cfg.signaling.url && !cfg.auth.jwtSecret) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['auth', 'jwtSecret'],
      message: 'signaling.url is set but auth.jwtSecret is missing — the agent must verify operator tokens itself',
    });
  }
});
export type AgentConfig = z.infer<typeof AgentConfigSchema>;

/**
 * Subset of the agent config the console needs. Never includes secrets.
 * A zod schema (not just an interface) because it travels the wire: the
 * agent serves it at /api/config on the LAN and pushes it over signaling
 * (`config` message) when the console can't reach the Pi directly.
 */
export const PublicConfigSchema = z.object({
  mode: z.literal('agent'),
  transport: z.enum(['webrtc', 'mjpeg-ws']),
  controlTickHz: z.number(),
  deadmanMs: z.number(),
  captureEncodeMs: z.number(),
  /** LAN listener wants a token in the hello (static token or JWT). */
  authRequired: z.boolean(),
  cameras: z.object({
    front: z.object({ enabled: z.boolean() }),
    rear: z.object({ enabled: z.boolean() }),
  }),
  iceServers: z.array(IceServerSchema),
});
export type PublicConfig = z.infer<typeof PublicConfigSchema>;

/** What the relay serves at /api/config: enough to run the login flow. */
export const RelayBootstrapSchema = z.object({
  mode: z.literal('relay'),
  authRequired: z.boolean(),
  diggerId: z.string(),
});
export type RelayBootstrap = z.infer<typeof RelayBootstrapSchema>;

/**
 * GET /api/config from either host. The console normalizes a missing `mode`
 * to 'agent' before parsing (pre-internet-release agents don't send it).
 */
export const ConsoleBootstrapSchema = z.discriminatedUnion('mode', [
  PublicConfigSchema,
  RelayBootstrapSchema,
]);
export type ConsoleBootstrap = z.infer<typeof ConsoleBootstrapSchema>;

export function toPublicConfig(cfg: AgentConfig): PublicConfig {
  return {
    mode: 'agent',
    transport: cfg.transport,
    controlTickHz: cfg.control.tickHz,
    deadmanMs: cfg.safety.deadmanMs,
    captureEncodeMs: cfg.hud.captureEncodeMs,
    authRequired: cfg.auth.required,
    cameras: {
      front: { enabled: cfg.cameras.front.source !== 'none' },
      rear: { enabled: cfg.cameras.rear.source !== 'none' },
    },
    iceServers: cfg.webrtc.iceServers,
  };
}
